import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import { MEMORY_PATH_NAME } from './memory.constants';
import {
  ListMemoryRecordsResponseSchema,
  ListMemorySessionsResponseSchema,
  ListMemoryEventsResponseSchema,
  CreateMemoryEventResponseSchema,
  GetMemoryRecordResponseSchema,
} from './memory.dto';
import {
  CreateMemoryEventPayloadSchema,
  CreateMemoryRecordPayloadSchema,
  ManualMemoryEntryPointQuerySchema,
  MarkMemoryRecordObsoleteBodySchema,
} from './memory.schema';
import { ManualMemoryEntryPointSchema } from './memory-entry-point.schema';

const MemoryByApplicationIdParamsSchema = z.object({
  applicationId: z.string(),
});

const MemoryByApplicationAndEndpointParamsSchema = z.object({
  applicationId: z.string(),
  endpointId: z.string(),
});

const EndpointMemoryRecordByIdParamsSchema = z.object({
  applicationId: z.string(),
  endpointId: z.string(),
  recordId: z.string(),
});

const MemoryRecordByIdParamsSchema = z.object({
  applicationId: z.string(),
  recordId: z.string(),
});

const UpdateMemoryRecordBodySchema = z.object({
  content: z.string().describe('The updated content for the memory record'),
  sessionId: z
    .string()
    .optional()
    .describe('Optional session ID for the correction event'),
  entryPoint: ManualMemoryEntryPointSchema.optional().describe(
    'The surface that sent the edit',
  ),
});

const ListMemoryRecordsQuerySchema = z.object({
  skip: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Number of records to skip (for pagination)'),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .max(250)
    .optional()
    .describe('Maximum number of records to return'),
  search: z.string().optional().describe('Search query for memory content'),
});

const ListMemorySessionsQuerySchema = z.object({
  skip: z.coerce.number().int().nonnegative().optional(),
  limit: z.coerce.number().int().positive().max(250).optional(),
});

const ListMemoryEventsQuerySchema = z.object({
  sessionId: z.string(),
  skip: z.coerce.number().int().nonnegative().optional(),
  limit: z.coerce.number().int().positive().max(250).optional(),
});

const RetrieveMemoriesQuerySchema = z.object({
  query: z.string().describe('Search query to retrieve relevant memories'),
  topK: z.coerce
    .number()
    .int()
    .positive()
    .max(20)
    .optional()
    .describe('Number of top results to return'),
});

export const MemoryContract = terraContract
  .prefix(`/${MEMORY_PATH_NAME}`)
  .router({
    listMemoryRecords: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/records',
        summary: 'List memory records for an application',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MemoryByApplicationIdParamsSchema,
          query: ListMemoryRecordsQuerySchema,
        }),
      )
      .output(ListMemoryRecordsResponseSchema),
    listEndpointMemoryRecords: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/endpoint/{endpointId}/records',
        summary: 'List memory records for a specific endpoint',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MemoryByApplicationAndEndpointParamsSchema,
          query: ListMemoryRecordsQuerySchema,
        }),
      )
      .output(ListMemoryRecordsResponseSchema),
    createEndpointMemoryRecord: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/endpoint/{endpointId}/records',
        summary: 'Create an endpoint-scoped memory record',
        description:
          'Creates a memory event scoped to a specific endpoint. Agent Core extracts and consolidates endpoint facts asynchronously.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: MemoryByApplicationAndEndpointParamsSchema,
          body: CreateMemoryRecordPayloadSchema,
        }),
      )
      .output(CreateMemoryEventResponseSchema),
    deleteEndpointMemoryRecord: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'DELETE',
        path: '/application/{applicationId}/endpoint/{endpointId}/records/{recordId}',
        summary: 'Delete an endpoint-scoped memory record',
        description:
          'Permanently deletes an endpoint memory record from Agent Core. This action cannot be undone.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: EndpointMemoryRecordByIdParamsSchema,
          query: ManualMemoryEntryPointQuerySchema.optional(),
        }),
      )
      .output(CreateMemoryEventResponseSchema),
    updateEndpointMemoryRecord: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'PUT',
        path: '/application/{applicationId}/endpoint/{endpointId}/records/{recordId}',
        summary:
          'Update an endpoint-scoped memory record by creating a correction event',
        description:
          'Updates an endpoint memory record by sending a correction event to Agent Core.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: EndpointMemoryRecordByIdParamsSchema,
          body: UpdateMemoryRecordBodySchema,
        }),
      )
      .output(CreateMemoryEventResponseSchema),
    getMemoryRecord: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/records/{recordId}',
        summary: 'Get a specific memory record',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: MemoryRecordByIdParamsSchema }))
      .output(GetMemoryRecordResponseSchema),
    updateMemoryRecord: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'PUT',
        path: '/application/{applicationId}/records/{recordId}',
        summary: 'Update a memory record by creating a correction event',
        description:
          'Updates a memory record by sending a correction event to Agent Core. The original record content is preserved and a new correction event is created.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MemoryRecordByIdParamsSchema,
          body: UpdateMemoryRecordBodySchema,
        }),
      )
      .output(CreateMemoryEventResponseSchema),
    deleteMemoryRecord: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'DELETE',
        path: '/application/{applicationId}/records/{recordId}',
        summary: 'Delete a memory record',
        description:
          'Permanently deletes a memory record from Agent Core. This action cannot be undone.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MemoryRecordByIdParamsSchema,
          query: ManualMemoryEntryPointQuerySchema.optional(),
        }),
      )
      .output(CreateMemoryEventResponseSchema),
    markMemoryRecordObsolete: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/records/{recordId}/obsolete',
        summary: 'Mark a memory record obsolete',
        description:
          'Archives a memory that is no longer true. The note is kept as evidence. Agents stop receiving the memory. The record and its history stay.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MemoryRecordByIdParamsSchema,
          body: MarkMemoryRecordObsoleteBodySchema,
        }),
      )
      .output(CreateMemoryEventResponseSchema),
    retrieveMemories: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/retrieve',
        summary: 'Retrieve relevant memories using semantic search',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MemoryByApplicationIdParamsSchema,
          query: RetrieveMemoriesQuerySchema,
        }),
      )
      .output(ListMemoryRecordsResponseSchema),
    listMemorySessions: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/sessions',
        summary: 'List memory sessions for an application',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MemoryByApplicationIdParamsSchema,
          query: ListMemorySessionsQuerySchema,
        }),
      )
      .output(ListMemorySessionsResponseSchema),
    listMemoryEvents: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/events',
        summary: 'List memory events for a session',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MemoryByApplicationIdParamsSchema,
          query: ListMemoryEventsQuerySchema,
        }),
      )
      .output(ListMemoryEventsResponseSchema),
    createMemoryEvent: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/events',
        summary: 'Create a new memory event (conversational)',
        description:
          'Creates a new conversational memory event in Agent Core. AWS will extract memories from this event asynchronously.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: MemoryByApplicationIdParamsSchema,
          body: CreateMemoryEventPayloadSchema,
        }),
      )
      .output(CreateMemoryEventResponseSchema),
    createMemoryRecord: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/records',
        summary: 'Create an application memory event',
        description:
          'Creates a conversational memory event in Agent Core. AWS extracts and consolidates long-term memories asynchronously. Returns sessionId for correlating the submission.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: MemoryByApplicationIdParamsSchema,
          body: CreateMemoryRecordPayloadSchema,
        }),
      )
      .output(CreateMemoryEventResponseSchema),
    listUserPreferenceRecords: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/user-preferences',
        summary: 'List user preference memory records for the current user',
        description:
          'Lists persistent user preference memories scoped to the current authenticated user within the given application. Each user only sees their own preferences.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: MemoryByApplicationIdParamsSchema }))
      .output(ListMemoryRecordsResponseSchema),
    createUserPreferenceRecord: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/user-preferences',
        summary: 'Create a user preference memory record',
        description:
          'Creates a persistent user preference memory scoped to the current authenticated user. These preferences are injected into Co-pilot sessions at startup.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: MemoryByApplicationIdParamsSchema,
          body: CreateMemoryRecordPayloadSchema,
        }),
      )
      .output(CreateMemoryEventResponseSchema),
    deleteUserPreferenceRecord: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'DELETE',
        path: '/application/{applicationId}/user-preferences/{recordId}',
        summary: 'Delete a user preference memory record',
        description:
          'Permanently deletes a user preference memory record. Uses the user-scoped namespace for existence verification, ensuring users can only delete their own preferences.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MemoryRecordByIdParamsSchema,
          query: ManualMemoryEntryPointQuerySchema.optional(),
        }),
      )
      .output(CreateMemoryEventResponseSchema),
    updateUserPreferenceRecord: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'PUT',
        path: '/application/{applicationId}/user-preferences/{recordId}',
        summary: 'Update a user preference memory record',
        description:
          'Updates a user preference memory record by deleting the existing record and creating a new one with the updated content. Uses the user-scoped namespace for isolation.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MemoryRecordByIdParamsSchema,
          body: UpdateMemoryRecordBodySchema,
        }),
      )
      .output(CreateMemoryEventResponseSchema),
  });
